import styles from './JobItem.less';

import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import ScrollMonitor from 'react-scrollmonitor';

import { jobDetailUrlPrefix, jobLogoUrlPrefix } from 'config';
import { parseHTML } from 'autosuggest-highlight';
import striptags from 'striptags';
import { ScreenReaderOnly } from 'seek-style-guide/react';
import JobLocation from 'JobLocation/JobLocation';
import JobClassification from 'JobClassification/JobClassification';
import { jobViewModel } from '@seek/seek-refine-job-search';
import ExternalLink from 'ExternalLink/ExternalLink';
import SaveJob from 'SaveJob/SaveJob';
import { trackJobImpression, trackJobImpressionExit, trackJobClick } from 'job-tracking';
import { resultLinkClicked } from 'store/results/results';

function highlightFromHTML(html) {
  return parseHTML(html, 'b')
    .map(({ highlight, text }, index) => {
      const cleanText = striptags(text);

      return highlight ?
        <strong className={styles.highlight} key={index}>{cleanText}</strong> :
        <span key={index}>{cleanText}</span>;
    });
}

function getJobDataAutomation(job) {
  const { isPremium, locationMatch } = job;

  if (isPremium) {
    return 'premiumJob';
  }

  if (locationMatch === 'Exact') {
    return 'tier1job';
  }

  if (locationMatch === 'Nearby') {
    return 'tier2job';
  }

  if (locationMatch === 'Area') {
    return 'tier3job';
  }

  return 'normalJob';
}

const mapStateToProps = ({ search, results, location }) => ({
  query: search.query,
  locale: location.locale,
  location: results.location,
  userQueryId: results.userQueryId
});

const mapDispatchToProps = dispatch => ({
  jobClicked: job => dispatch(resultLinkClicked(job))
});

@connect(mapStateToProps, mapDispatchToProps)
export default class JobItem extends Component {

  static propTypes = {
    job: PropTypes.object.isRequired,
    isFirst: PropTypes.bool.isRequired,
    isLast: PropTypes.bool.isRequired,
    query: PropTypes.object.isRequired,
    location: PropTypes.object,
    locale: PropTypes.string.isRequired,
    userQueryId: PropTypes.string.isRequired,
    jobClicked: PropTypes.func.isRequired
  };

  constructor() {
    super();

    this.resultImpression = this.resultImpression.bind(this);
    this.resultImpressionExit = this.resultImpressionExit.bind(this);
    this.resultClick = this.resultClick.bind(this);

    // Track ENTER/EXIT events so that EXIT events aren't tracked without a
    // corresponding ENTER event.
    this.inViewport = false;
  }

  shouldComponentUpdate(nextProps) {
    const { job: nextJob, location: nextLocation } = nextProps;
    const { job: prevJob, location: prevLocation } = this.props;
    return nextJob.id !== prevJob.id || nextLocation.locationId !== prevLocation.locationId;
  }

  resultImpression() {
    const { job: { tracking } } = this.props;

    // NOTE: ScrollMonitor (see `on` method in scrollmonitor used by react-scrollmonitor)
    //       will trigger EXIT events, at DOM element mount time, for items fully above the viewport.
    //       There will be no corresponding ENTER event for these.
    //
    // Track entry of the JobItem into the viewport. EXIT events will be ignored until this is true.
    this.inViewport = true;

    trackJobImpression(tracking);
  }

  resultImpressionExit() {
    const { job: { tracking } } = this.props;

    // Track EXIT events if the DOM element has already entered the viewport.
    if (this.inViewport) {
      this.inViewport = false;

      trackJobImpressionExit(tracking);
    }
  }

  resultClick() {
    const { jobClicked, job } = this.props;

    trackJobClick(job.tracking);

    return jobClicked(job);
  }

  render() {
    const {
      isFirst, isLast, query, locale,
      location, job, userQueryId
    } = this.props;
    const {
      id, isPremium, isStandOut, title, advertiser, logo, isFresh,
      listingDate, listingDateDescription, bulletPoints, teaser,
      classification, salary, workType, schema, queryString,
      joraImpressionTrackingUrl, location: jobLocation
    } = jobViewModel({ job, query, location, locale });
    const jobClassName = classnames({
      [styles.root]: true,
      [styles.root_isPremium]: isPremium,
      [styles.root_isStandOut]: isStandOut,
      [styles.root_isClassic]: !isPremium && !isStandOut,
      [styles.root_isFirst]: isFirst,
      [styles.root_isLast]: isLast
    });
    const featuredClassName = classnames(styles.badge, styles.featured);
    const listingDateClassName = classnames({
      [styles.badge]: true,
      [styles.dateListed]: true,
      [styles.freshJob]: isFresh
    });

    /* eslint-disable react/no-danger */
    return (
      <ScrollMonitor enterViewport={this.resultImpression} exitViewport={this.resultImpressionExit}>
        <article id={id} className={jobClassName} data-automation={getJobDataAutomation(job)}>
          {
            joraImpressionTrackingUrl &&
              <img src={joraImpressionTrackingUrl} className={styles.trackingPixel} />
          }

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
          />

          {
            isPremium &&
              <span className={featuredClassName}>
                <ScreenReaderOnly>This is a featured job</ScreenReaderOnly>
                <span aria-hidden="true">Featured</span>
              </span>
          }
          {
            listingDate &&
              <span className={listingDateClassName}>
                <ScreenReaderOnly>{listingDateDescription}</ScreenReaderOnly>
                <span aria-hidden="true" data-automation="jobListingDate">{listingDate}</span>
              </span>
          }

          <h1 className={styles.title}>
            <ExternalLink
              intercept={this.resultClick}
              className={styles.titleLink}
              href={`${jobDetailUrlPrefix}${id}`}
              query={{ ...queryString, userqueryid: userQueryId, ref: 'beta' }}
              data-automation="jobTitle">
              {highlightFromHTML(title)}
            </ExternalLink>
          </h1>

          <p className={styles.advertiser}>
            <ScreenReaderOnly>at </ScreenReaderOnly>
            <span data-automation="jobAdvertiser">{advertiser.description}</span>
          </p>

          <div className={styles.content}>
            <div className={styles.primary}>
              <ScreenReaderOnly>
                <p>This is a {workType} job</p>
              </ScreenReaderOnly>
              {
                bulletPoints &&
                  <ul className={styles.bullets}>
                    {
                      bulletPoints.map((option, i) =>
                        <li className={styles.bullet} key={i}>
                          {highlightFromHTML(option)}
                        </li>
                      )
                    }
                  </ul>
              }
              <p className={styles.shortDescription} data-automation="jobShortDescription">
                {highlightFromHTML(teaser)}
              </p>
              <div className={styles.footer}>
                <div className={styles.classification}>
                  <JobClassification {...classification} />
                </div>
                <div className={styles.saveJob}>
                  <SaveJob id={id} />
                </div>
              </div>
            </div>

            <div className={styles.secondary}>
              <div>
                {
                  salary &&
                    <p className={styles.salary} data-automation="jobSalary">
                      {salary}
                    </p>
                }
                <JobLocation {...jobLocation} />
              </div>
              {
                logo.description &&
                  <img
                    className={styles.logo}
                    data-automation="jobLogo"
                    src={`${jobLogoUrlPrefix}/${logo.description}`}
                    alt="Company logo"
                    aria-hidden="true"
                  />
              }
            </div>
          </div>
        </article>
      </ScrollMonitor>
    );
    /* eslint-enable react/no-danger */
  }
}



/** WEBPACK FOOTER **
 ** ./app/components/SearchResultPage/components/SearchResultList/components/JobItem/JobItem.js
 **/